import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { devices, firmware as fwApi, customers, type Port, type FirmwareRelease, type Device } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { relTime, uptime } from '@/lib/format';
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis,
  Tooltip, CartesianGrid,
} from 'recharts';
import {
  RefreshCw, Power, Gauge, Activity, Terminal,
  ChevronLeft, Sparkles, Edit3, Cable, Wifi as WifiIcon, Globe,
  MoreHorizontal, Wifi, WifiOff,
} from 'lucide-react';

// Date locale au format YYYY-MM-DD (jamais toISOString qui renvoie de l'UTC).
function localYmd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const PARAM_PRESETS: Array<{ label: string; path: string; placeholder?: string; type?: 'text' | 'number' | 'boolean' }> = [
  { label: 'SSID 2.4 GHz',              path: 'InternetGatewayDevice.LANDevice.1.WLANConfiguration.1.SSID',           placeholder: 'Luciole-XXXX' },
  { label: 'Password WiFi 2.4 GHz',     path: 'InternetGatewayDevice.LANDevice.1.WLANConfiguration.1.PreSharedKey.1.KeyPassphrase' },
  { label: 'SSID 5 GHz',                path: 'InternetGatewayDevice.LANDevice.1.WLANConfiguration.9.SSID',           placeholder: 'Luciole-XXXX_5G' },
  { label: 'Password WiFi 5 GHz',       path: 'InternetGatewayDevice.LANDevice.1.WLANConfiguration.9.PreSharedKey.1.KeyPassphrase' },
  { label: 'Periodic inform interval',  path: 'InternetGatewayDevice.ManagementServer.PeriodicInformInterval',        type: 'number', placeholder: '300' },
  { label: 'X_000631 mgmt URL',         path: 'InternetGatewayDevice.X_000631_ManagementServer.URL' },
  { label: 'SIP proxy',                 path: 'InternetGatewayDevice.Services.VoiceService.1.VoiceProfile.1.SIP.ProxyServer' },
];

export default function DeviceDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const deviceId = Number(id);
  const [editOpen, setEditOpen] = useState(false);
  const [wifiOpen, setWifiOpen] = useState(false);
  const [applyConfirmOpen, setApplyConfirmOpen] = useState(false);
  const [fwUpdateOpen, setFwUpdateOpen] = useState(false);

  const { data: device } = useQuery({
    queryKey: ['device', deviceId],
    queryFn: () => devices.get(deviceId),
  });

  const { data: events } = useQuery({
    queryKey: ['device-events', deviceId],
    queryFn: () => devices.events(deviceId),
    enabled: !!device,
  });

  const { data: metrics } = useQuery({
    queryKey: ['device-metrics', deviceId],
    queryFn: () => devices.metrics(deviceId, 24),
    enabled: !!device,
    refetchInterval: 30_000,
  });

  // Période partagée entre ConsumptionCard et BandwidthChart. Default = 30j.
  // Dates en heure LOCALE (pas toISOString qui renvoie UTC : « Aujourd'hui »
  // après 20h Montréal basculait sur la journée UTC du lendemain → carte vide).
  const today = localYmd(new Date());
  const daysAgo = (n: number) => localYmd(new Date(Date.now() - n * 86400000));
  const [periodFrom, setPeriodFrom] = useState(daysAgo(30));
  const [periodTo, setPeriodTo]     = useState(today);
  // Calcule hours pour la requête bandwidth (l'API attend des hours, pas from/to)
  const periodHours = Math.max(1, Math.ceil((new Date(periodTo + 'T23:59:59').getTime() - new Date(periodFrom + 'T00:00:00').getTime()) / 3600000));

  const { data: bandwidth } = useQuery({
    queryKey: ['device-bandwidth', deviceId, periodHours],
    queryFn: () => devices.bandwidth(deviceId, periodHours),
    enabled: !!device,
    refetchInterval: 30_000,
  });
  // Label humain pour le titre du graph
  const periodLabel = (() => {
    if (periodFrom === today && periodTo === today) return "aujourd'hui";
    if (periodFrom === daysAgo(7))  return '7 derniers jours';
    if (periodFrom === daysAgo(30)) return '30 derniers jours';
    if (periodFrom === daysAgo(90)) return '90 derniers jours';
    if (periodHours <= 24) return `${periodHours}h`;
    return `${periodFrom} → ${periodTo}`;
  })();
  // Données + durée du graphe bandwidth — la durée pilote le format de l'axe X
  // (dates sur multi-jours, heures en intraday). On borne l'axe sur la période
  // DEMANDÉE (periodHours), pas seulement sur l'étendue des points, pour que
  // l'axe change vraiment quand on change de période même si la donnée est éparse.
  const bwData = bandwidthChartData(bandwidth);
  const bwSpanMs = periodHours * 3_600_000;

  const { data: portsData } = useQuery({
    queryKey: ['device-ports', deviceId],
    queryFn: () => devices.ports(deviceId),
    enabled: !!device,
    refetchInterval: 15_000,
  });

  const refreshPorts = useMutation({
    mutationFn: () =>
      toast.promise(devices.refreshPorts(deviceId), {
        loading: 'Refresh en cours…',
        success: 'Refresh pushé au CPE — réponse au prochain Inform (~60s max)',
      }),
    onSuccess: () => {
      // Invalide tout après ~6s pour laisser le CPE répondre
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['device-ports', deviceId] });
        qc.invalidateQueries({ queryKey: ['device-hosts', deviceId] });
        qc.invalidateQueries({ queryKey: ['device', deviceId] });
      }, 6000);
    },
  });

  const applyTpl = useMutation({
    mutationFn: () =>
      toast.promise(devices.applyTemplate(deviceId), {
        loading: 'Application du template…',
        success: 'Template poussé via filosACS NBI',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['device', deviceId] }),
  });

  const rebootMut = useMutation({
    mutationFn: () =>
      toast.promise(devices.reboot(deviceId), {
        loading: 'Reboot en cours d’envoi…',
        success: 'Reboot queued — CPE down ~60-90s',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['device-events', deviceId] }),
  });
  const speedtestMut = useMutation({
    mutationFn: () =>
      toast.promise(devices.speedtest(deviceId), {
        loading: 'Speedtest TR-143 en cours d’envoi…',
        success: 'Test lancé — le résultat s’affiche au prochain contact du CPE',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['device-events', deviceId] });
      qc.invalidateQueries({ queryKey: ['device-speedtests', deviceId] });
    },
  });
  const wanPingMut = useMutation({
    mutationFn: (enable: boolean) =>
      toast.promise(devices.wanPing(deviceId, enable), {
        loading: enable ? 'Activation WAN ping…' : 'Désactivation WAN ping…',
        success: (r) => `WAN ping ${r.enable ? 'activé' : 'désactivé'}`,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['device-events', deviceId] }),
  });
  const [webAccess, setWebAccess] = useState<{ kind: 'direct' | 'proxy'; url: string | null; username: string | null; password: string | null; expires_at: string; port_ready?: boolean; wait_note?: string | null } | null>(null);
  const openWebMut = useMutation({
    // Backend poll le port 8443 du CPE jusqu'à 30s, puis retourne kind=direct + creds.
    // Modal s'ouvre seulement après que le port soit ready (port_ready=true).
    // Plus de pré-ouverture de tab blanche — l'user clique l'URL dans le modal,
    // qui ouvre un vrai tab avec une vraie URL.
    mutationFn: () =>
      toast.promise(devices.openWebAccess(deviceId), {
        loading: 'Activation accès WAN sur le CPE (jusqu\'à 30s)…',
        success: (r) => r.port_ready ? 'Accès WAN prêt' : 'Accès demandé (port pas encore ouvert)',
      }),
    onSuccess: (r) => {
      setWebAccess(r);
      qc.invalidateQueries({ queryKey: ['device-events', deviceId] });
    },
  });
  const handleOpenWebAccess = () => {
    openWebMut.mutate();
  };
  const closeWebMut = useMutation({
    mutationFn: () =>
      toast.promise(devices.closeWebAccess(deviceId), {
        loading: 'Fermeture accès Web…',
        success: 'Accès Web fermé',
      }),
    onSuccess: () => {
      setWebAccess(null);
      qc.invalidateQueries({ queryKey: ['device-events', deviceId] });
    },
  });

  const adoptMut = useMutation({
    mutationFn: () =>
      toast.promise(devices.adopt(deviceId), {
        loading: 'Adoption en cours (Phase 1 docking)…',
        success: (r) => `Adopté via ${r.driver} (${Object.keys(r.changes).length} param(s) modifié(s))`,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['device', deviceId] }),
  });
  const unadoptMut = useMutation({
    mutationFn: () =>
      toast.promise(devices.unadopt(deviceId), {
        loading: 'Désadoption (rollback Phase 1)…',
        success: 'Device désadopté — config originale restaurée',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['device', deviceId] }),
  });
  const markProvMut = useMutation({
    mutationFn: (customerId: number) =>
      toast.promise(devices.markProvisioned(deviceId, customerId), {
        loading: 'Mark as provisioned (import depuis ACS existant)…',
        success: 'Provisionné en mode import (config préservée)',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['device', deviceId] }),
  });

  const wifiMut = useMutation({
    mutationFn: (items: Array<{ index: string; ssid?: string; password?: string }>) =>
      toast.promise(devices.setWifi(deviceId, items), {
        loading: 'Push WiFi en cours…',
        // Honnête : la task est queuée, le CPE applique à son prochain contact.
        success: (r) => `WiFi : ${r.pushed} param(s) poussé(s) — appliqué au prochain Inform (≤15 min)`,
        error: (e) => `Échec du push WiFi : ${(e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? (e as Error).message}`,
      }),
    onSuccess: () => {
      setWifiOpen(false);
      qc.invalidateQueries({ queryKey: ['device-events', deviceId] });
      // Re-lire les ports : à 60s (si le Connection Request a réussi) puis à
      // 16 min (après l'Inform périodique naturel, PII 900s SmartRG).
      setTimeout(() => qc.invalidateQueries({ queryKey: ['device-ports', deviceId] }), 60_000);
      setTimeout(() => qc.invalidateQueries({ queryKey: ['device-ports', deviceId] }), 16 * 60_000);
    },
  });

  if (!device) {
    return (
      <>
        <PageHeader title="Device" />
        <div className="p-8 text-text-faint">Chargement…</div>
      </>
    );
  }

  // Capabilities exposées par le driver backend (calix / smartrg)
  const supportsSpeedtest = portsData?.capabilities?.supportsSpeedtest ?? false;

  return (
    <>
      <PageHeader
        title={device.serial_number}
        subtitle={(device.metadata as Record<string, unknown> | undefined)?.hardware_model as string ?? device.model?.model_name ?? ''}
        actions={
          <>
            <button className="btn btn-sm" onClick={() => refreshPorts.mutate()} disabled={refreshPorts.isPending}>
              <RefreshCw size={11} className={refreshPorts.isPending ? 'animate-spin' : ''} />
              {refreshPorts.isPending ? 'Refresh…' : 'Forcer refresh'}
            </button>
            <button
              className="btn btn-sm text-danger"
              onClick={() => { if (window.confirm('Reboot le CPE maintenant ? ~60-90s indisponible.')) rebootMut.mutate(); }}
              disabled={rebootMut.isPending}>
              <Power size={11} />{rebootMut.isPending ? 'Reboot…' : 'Reboot'}
            </button>
            <button
              className={`btn btn-sm ${supportsSpeedtest ? '' : 'opacity-40 cursor-not-allowed'}`}
              onClick={() => supportsSpeedtest && speedtestMut.mutate()}
              disabled={!supportsSpeedtest || speedtestMut.isPending}
              title={supportsSpeedtest ? undefined : 'Pas supporté par ce firmware (Calix n’expose pas TR-143)'}>
              <Gauge size={11} />{speedtestMut.isPending ? 'Speedtest…' : 'Speedtest'}
            </button>
            <button
              className="btn btn-sm text-info"
              onClick={handleOpenWebAccess}
              disabled={openWebMut.isPending}
              title="Ouvre le Web UI du CPE — accès temporaire (~30 min)">
              <Terminal size={11} />{openWebMut.isPending ? 'Ouverture…' : 'Accès Web'}
            </button>
            <ActionMenu
              items={[
                ...(device.bootstrap_state === 'discovered' ? [{
                  label: 'Adopter',
                  icon: <Edit3 size={11} />,
                  onClick: () => {
                    if (window.confirm('Adopter ce device ? Phase 1 = push config technique seulement (URL ACS + Inform interval). Aucun changement customer.')) adoptMut.mutate();
                  },
                }] : []),
                ...(device.bootstrap_state === 'adopted' ? [{
                  label: 'Provisionner',
                  icon: <Edit3 size={11} />,
                  onClick: () => setEditOpen(true),
                }] : []),
                ...(device.bootstrap_state === 'provisioned' ? [{
                  label: 'Modifier la config',
                  icon: <Edit3 size={11} />,
                  onClick: () => setEditOpen(true),
                }] : []),
                { label: 'Apply template…', onClick: () => setApplyConfirmOpen(true), disabled: applyTpl.isPending, icon: <Sparkles size={11} /> },
                { label: 'Mettre à jour firmware…', onClick: () => setFwUpdateOpen(true), icon: <Cable size={11} /> },
                ...(device.bootstrap_state === 'adopted' ? [{
                  label: 'Désadopter',
                  danger: true,
                  onClick: () => { if (window.confirm('Désadopter ? Le device sera re-pointé vers Calix Cloud / NOC, perdras le contrôle ACS.')) unadoptMut.mutate(); },
                }] : []),
              ]}
            />
          </>
        }
      />

      <div className="flex-1 overflow-auto">
        <div className="border-b border-border bg-bg-2 px-4 py-2 flex items-center gap-2 text-xs">
          <Link to="/app/devices" className="text-text-muted hover:text-text flex items-center gap-1">
            <ChevronLeft size={11} />Tous les devices
          </Link>
        </div>

        <div className="p-4 grid grid-cols-[1fr_320px] gap-4 max-w-[1600px]">
          {/* Left: charts + table */}
          <div className="space-y-4 min-w-0">
            {/* Stat tiles */}
            <div className="grid grid-cols-6 gap-3">
              <Tile label="Status"      value={device.is_online ? 'Online' : 'Offline'} colorClass={device.is_online ? 'text-success' : 'text-danger'} />
              <Tile label="Uptime"      value={uptime(device.uptime_seconds)} />
              <Tile label="Firmware"    value={device.firmware_version ?? '—'} mono />
              <Tile label="Last inform" value={relTime(device.last_inform_at)} />
              <Tile label="Customer"    value={device.customer?.name ?? '—'} />
              <Tile label="Bootstrap"   value={device.bootstrap_state ?? '—'} />
            </div>

            {/* Health monitor — règles heuristiques sur les 24h, ouvre la voie à AI v2 */}
            <HealthCard deviceId={deviceId} />

            {/* Network info */}
            <NetworkCard
              network={portsData?.network}
              onWanPing={(enable) => wanPingMut.mutate(enable)}
              wanPingPending={wanPingMut.isPending}
            />

            {/* Ports state grid */}
            <PortsCard
              ports={portsData?.ports ?? []}
              lastInform={portsData?.last_inform ?? null}
              onRefresh={() => refreshPorts.mutate()}
              refreshing={refreshPorts.isPending}
            />

            {/* WiFi: SSID + password éditables par bande */}
            <WifiCard ports={portsData?.ports ?? []} driver={portsData?.driver} onEdit={() => setWifiOpen(true)} />

            {/* Speedtests — disabled si le CPE ne supporte pas TR-143 (ex: Calix) */}
            <SpeedtestCard
              deviceId={deviceId}
              onRun={() => speedtestMut.mutate()}
              running={speedtestMut.isPending}
              supported={supportsSpeedtest}
            />

            {/* Hosts connectés */}
            <HostsCard deviceId={deviceId} />

            {/* Customer assignment + notes */}
            <CustomerNotesCard deviceId={deviceId} device={device} />

            {/* Uptime history — quand le device est en ligne / offline */}
            <UptimeHistoryCard deviceId={deviceId} />

            {/* Consumption summary par période */}
            <ConsumptionCard
              deviceId={deviceId}
              from={periodFrom} to={periodTo}
              setFrom={setPeriodFrom} setTo={setPeriodTo}
            />

            {/* Bandwidth chart — real data from bandwidth_stats */}
            <ChartCard title={`Bandwidth WAN · ${periodLabel}`}
                       footer={`Source: bandwidth_stats (${bandwidth?.length ?? 0} points collectés). PeriodicInform = ${
                         portsData?.network?.periodic_inform_sec
                           ? `${Math.round(portsData.network.periodic_inform_sec / 60)} min`
                           : '?'
                       } → ~${portsData?.network?.periodic_inform_sec ? Math.round(86400 / portsData.network.periodic_inform_sec) : '?'} points/jour.`}>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={bwData}>
                  <CartesianGrid stroke="#2e3038" strokeDasharray="2 2" />
                  <XAxis dataKey="t" type="number" scale="time" domain={['dataMin', 'dataMax']}
                    stroke="#6c7079" fontSize={10}
                    tickFormatter={timeTickFmt(bwSpanMs)} ticks={timeTicks(bwData, bwSpanMs)} />
                  <YAxis stroke="#6c7079" fontSize={10} unit=" Mbps" />
                  <Tooltip
                    contentStyle={{ background: '#21232a', border: '1px solid #3a3c46', fontSize: 11 }}
                    labelFormatter={fmtTooltipTime}
                  />
                  <Line type="monotone" dataKey="dl_mbps" stroke="#56b4e9" strokeWidth={1.5} dot={false} name="Download Mbps" />
                  <Line type="monotone" dataKey="ul_mbps" stroke="#009e73" strokeWidth={1.5} dot={false} name="Upload Mbps" />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* WiFi Health dashboard — 3 charts par bande pour diagnostic */}
            <div className="card">
              <div className="px-3 py-2.5 border-b border-border">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <WifiIcon size={13} className="text-text-muted" />Santé WiFi · 24h
                </h3>
              </div>
              <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                <MiniChart
                  title="Noise (plancher de bruit)"
                  hint="Plus négatif = mieux. Au-dessus de -85 dBm = bruit ambiant élevé"
                  unit="dBm"
                  data={metricsChartData(metrics, ['wifi.radio.2g.noise_level', 'wifi.radio.5g.noise_level'])}
                  lines={[
                    { key: 'wifi.radio.2g.noise_level', color: '#e69f00', name: '2.4G' },
                    { key: 'wifi.radio.5g.noise_level', color: '#009e73', name: '5G' },
                  ]}
                  yDomain={[-100, -60]}
                />
                <MiniChart
                  title="Channel utilization"
                  hint="Trafic WiFi sur le canal. >70% = canal saturé, latence ↑"
                  unit="%"
                  data={metricsChartData(metrics, ['wifi.radio.2g.channel_util', 'wifi.radio.5g.channel_util'])}
                  lines={[
                    { key: 'wifi.radio.2g.channel_util', color: '#e69f00', name: '2.4G' },
                    { key: 'wifi.radio.5g.channel_util', color: '#009e73', name: '5G' },
                  ]}
                  yDomain={[0, 100]}
                />
                <MiniChart
                  title="Interference time"
                  hint="Temps où le canal subissait de l'interférence. Pics = signe de voisin/micro-ondes"
                  unit="%"
                  data={metricsChartData(metrics, ['wifi.radio.2g.interference_time', 'wifi.radio.5g.interference_time'])}
                  lines={[
                    { key: 'wifi.radio.2g.interference_time', color: '#e69f00', name: '2.4G' },
                    { key: 'wifi.radio.5g.interference_time', color: '#009e73', name: '5G' },
                  ]}
                  yDomain={[0, 100]}
                />
              </div>
            </div>

            {/* CPE Health dashboard */}
            <div className="card">
              <div className="px-3 py-2.5 border-b border-border">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <Activity size={13} className="text-text-muted" />Santé CPE · 24h
                </h3>
              </div>
              <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                <MiniChart
                  title="Hosts connectés"
                  hint="Nombre d'appareils connectés. Pics = heures de pointe"
                  unit=""
                  data={metricsChartData(metrics, ['hosts.count'])}
                  lines={[{ key: 'hosts.count', color: '#56b4e9', name: 'hosts' }]}
                />
                <MiniChart
                  title="CPU utilisation"
                  hint=">80% prolongé = surcharge"
                  unit="%"
                  data={metricsChartData(metrics, ['device.cpu_usage_pct'])}
                  lines={[{ key: 'device.cpu_usage_pct', color: '#cc79a7', name: 'CPU' }]}
                  yDomain={[0, 100]}
                />
                <MiniChart
                  title="Mémoire libre"
                  hint="En kB. Si proche de 0 → reboot recommandé"
                  unit="kB"
                  data={metricsChartData(metrics, ['device.memory_free_kb'])}
                  lines={[{ key: 'device.memory_free_kb', color: '#0072b2', name: 'free kB' }]}
                />
              </div>
            </div>

            {/* Events table */}
            <div className="card">
              <div className="px-3 py-2.5 border-b border-border flex items-center justify-between">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <Activity size={13} className="text-text-muted" />Events récents
                </h3>
                <span className="text-text-faint text-xs">{events?.length ?? 0} entries</span>
              </div>
              <table className="luc-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Type</th>
                    <th>Code</th>
                    <th>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {events?.slice(0, 20).map((e, i) => (
                    <tr key={i}>
                      <td className="font-mono text-[11px] text-text-muted">{new Date(e.time).toLocaleString()}</td>
                      <td><span className="badge">{e.event_type}</span></td>
                      <td className="text-xs">{e.event_code ?? '—'}</td>
                      <td className="text-[11px] text-text-faint truncate max-w-md">{e.data ?? '—'}</td>
                    </tr>
                  ))}
                  {!events?.length && (
                    <tr><td colSpan={4} className="text-center py-6 text-text-faint">Aucun event capturé.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right rail: device info + quick actions */}
          <div className="space-y-4">
            <InfoCard title="Device" rows={[
              ['Manufacturer',     device.model?.manufacturer ?? '—'],
              // Model = le plus spécifique connu. metadata.hardware_model vient
              // du NBI DeviceInfo.ModelName (GS4220E, GS5229E, SR905acv, etc.).
              // Fallback sur device_models.model_name si NBI pas encore refresh.
              ['Model',            (device.metadata as Record<string, unknown> | undefined)?.hardware_model as string ?? device.model?.model_name ?? '—'],
              ['Data model',       portsData?.driver === 'smartrg' ? 'TR-181' : portsData?.driver === 'calix' ? 'TR-098' : '—'],
              ['Serial',           device.serial_number],
              ['MAC',              device.mac_address ?? '—'],
              ['HW version',       device.hardware_version ?? '—'],
              ['Firmware',         device.firmware_version ?? '—'],
              [],
              ['Inform interval',  portsData?.network?.periodic_inform_sec
                ? `${portsData.network.periodic_inform_sec}s (${Math.round(portsData.network.periodic_inform_sec / 60)} min)`
                : '—'],
              ['Customer',         device.customer?.name ?? '—'],
              ['External ID',      device.customer?.external_id ?? '—'],
              [],
              ['Bootstrap state',  device.bootstrap_state ?? '—'],
              ['Template applied', device.provision_template_id ? `#${device.provision_template_id}` : 'none'],
            ]} />

            <InfoCard title="Notes" rows={[
              ['Ajoute une note', 'à venir Phase 3'],
            ]} />
          </div>
        </div>
      </div>

      <EditParamsModal deviceId={deviceId} open={editOpen} onClose={() => setEditOpen(false)} />
      <WebAccessModal
        info={webAccess}
        onClose={() => setWebAccess(null)}
        onCloseEarly={() => closeWebMut.mutate()}
        closing={closeWebMut.isPending}
      />
      <WifiEditModal
        open={wifiOpen}
        onClose={() => setWifiOpen(false)}
        ports={portsData?.ports ?? []}
        onSubmit={(params) => wifiMut.mutate(params)}
        submitting={wifiMut.isPending}
      />
      {applyConfirmOpen && (
        <ApplyTemplateConfirmModal
          deviceId={deviceId}
          onClose={() => setApplyConfirmOpen(false)}
          onConfirm={() => { setApplyConfirmOpen(false); applyTpl.mutate(); }}
        />
      )}
      {fwUpdateOpen && device && (
        <FirmwareUpdateModal
          device={device}
          onClose={() => setFwUpdateOpen(false)}
        />
      )}
    </>
  );
}

// ─── Web Access Modal (accès WAN temporaire) ───────────────────────────────
function WebAccessModal({
  info, onClose, onCloseEarly, closing,
}: {
  info: { kind: 'direct' | 'proxy'; url: string | null; username: string | null; password: string | null; expires_at: string; port_ready?: boolean; wait_note?: string | null; proxy_url?: string | null } | null;
  onClose: () => void;
  onCloseEarly: () => void;
  closing: boolean;
}) {
  const [now, setNow] = useState(Date.now());
  const [copied, setCopied] = useState<string | null>(null);
  // Tick le countdown chaque seconde
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!info) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [info?.expires_at]);

  if (!info) return null;

  const expiresMs = new Date(info.expires_at).getTime();
  const remainingMs = Math.max(0, expiresMs - now);
  const remainMin = Math.floor(remainingMs / 60000);
  const remainSec = Math.floor((remainingMs % 60000) / 1000);
  const expired = remainingMs <= 0;

  async function copy(label: string, val: string) {
    try { await navigator.clipboard.writeText(val); setCopied(label); setTimeout(() => setCopied(null), 1500); } catch {}
  }

  return (
    <Modal open onClose={onClose} title="Accès Web temporaire" width="max-w-lg">
      <div className="space-y-3 text-xs">
        {expired ? (
          <div className="bg-danger/10 border border-danger/40 rounded p-3 text-danger">
            ⏱ Session expirée. L'accès Web a été refermé automatiquement.
          </div>
        ) : (
          <div className="bg-success/10 border border-success/40 rounded p-3">
            <div className="text-success font-medium">✓ Accès actif</div>
            <div className="text-text-muted mt-1">
              Fermeture automatique dans <span className="font-mono font-medium">{String(remainMin).padStart(2, '0')}:{String(remainSec).padStart(2, '0')}</span>
            </div>
          </div>
        )}

        {info.port_ready === false && info.wait_note && (
          <div className="bg-warning/10 border border-warning/40 rounded p-2.5 text-warning">
            ⚠ {info.wait_note}
          </div>
        )}

        <div className="space-y-2">
          {/* Bouton "Ouvrir" — utilise proxy_url si dispo (auto-login SSO),
              sinon fallback sur info.url (l'user devra log manuellement). */}
          {(info.proxy_url || info.url) && (
            <a
              href={info.proxy_url || info.url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary w-full justify-center"
            >
              {info.proxy_url ? '🔒 Ouvrir Web UI CPE (auto-login)' : 'Ouvrir Web UI CPE (login manuel)'} →
            </a>
          )}
          {info.proxy_url && (
            <div className="text-[10.5px] text-text-faint text-center">
              Tunnel SSO via <code className="font-mono">cpe-{'{id}'}.acs.filos.ca</code> — token éphémère, cookie scopé.
            </div>
          )}

          {/* Username/Password — fallback si pas de proxy (login manuel) */}
          {!info.proxy_url && info.kind === 'direct' && info.username && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10.5px] uppercase tracking-wider text-text-faint mb-1">Username</div>
                <div className="flex items-center gap-1">
                  <code className="font-mono text-xs bg-bg-2 px-2 py-1 rounded border border-border flex-1">{info.username}</code>
                  <button onClick={() => copy('u', info.username!)} className="btn btn-sm">{copied === 'u' ? '✓' : 'copier'}</button>
                </div>
              </div>
              <div>
                <div className="text-[10.5px] uppercase tracking-wider text-text-faint mb-1">Password</div>
                <div className="flex items-center gap-1">
                  <code className="font-mono text-xs bg-bg-2 px-2 py-1 rounded border border-border flex-1 truncate" title={info.password ?? ''}>{info.password ?? '—'}</code>
                  <button onClick={() => copy('p', info.password ?? '')} className="btn btn-sm">{copied === 'p' ? '✓' : 'copier'}</button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-bg-2 border border-border rounded p-2.5 text-[11px] text-text-muted">
          {info.kind === 'proxy' ? (
            <>🔒 <strong>Tunnel sécurisé</strong> via subdomain dédié <code className="font-mono text-[10.5px]">cpe-X.acs.filos.ca</code>. Cookie scopé au tunnel, pas de leak sur acs.filos.ca. Token éphémère 30 min.</>
          ) : (
            <>⚠ <strong>Accès WAN direct</strong> — Web UI exposé sur l'IP publique du CPE. Auto-fermeture après 30 min.</>
          )}
          {' '}
          <button onClick={onCloseEarly} disabled={closing || expired} className="underline text-warning hover:text-warning/80">Fermer maintenant</button>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn btn-sm">Fermer fenêtre</button>
        </div>
      </div>
    </Modal>
  );
}

// ─── WiFi card ─────────────────────────────────────────────────────────────
function WifiCard({ ports, driver, onEdit }: { ports: Port[]; driver?: string; onEdit: () => void }) {
  const wifi = ports.filter((p) => p.type === 'wifi' && p.ssid);
  const [reveal, setReveal] = useState<Record<string, boolean>>({});
  const [copied, setCopied] = useState<string | null>(null);
  // Sur Calix (ExOS/GigaSpire), KeyPassphrase est write-only : le firmware ne
  // retourne jamais le password en lecture. Sur SmartRG il est lisible (base64).
  const isCalix = (driver ?? '').startsWith('calix');
  if (wifi.length === 0) return null;

  async function copy(idx: string, val: string) {
    try { await navigator.clipboard.writeText(val); setCopied(idx); setTimeout(() => setCopied(null), 1500); } catch {}
  }

  return (
    <div className="card">
      <div className="px-3 py-2.5 border-b border-border flex items-center justify-between">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <WifiIcon size={13} className="text-text-muted" />WiFi
        </h3>
        <button className="btn btn-sm btn-primary" onClick={onEdit}>
          <Edit3 size={11} />Modifier SSID / password
        </button>
      </div>
      <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
        {wifi.map((p) => {
          const band = p.band ?? bandFromStandard(p.standard, p.channel);
          const isOn = p.enable === true || p.enable === 'true' || p.enable === '1';
          const shown = reveal[p.index];
          const pw = p.passphrase ?? '';
          return (
            <div key={p.index} className="bg-bg-2 border border-border rounded px-3 py-2 space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium truncate" title={p.ssid ?? ''}>{p.ssid}</div>
                  <div className="text-[10.5px] text-text-faint">
                    {band} · ch {p.channel ?? '—'} · {p.standard ?? ''} · idx {p.index}
                  </div>
                </div>
                <span className={`dot ${isOn ? 'dot-up' : 'dot-down'}`} />
              </div>
              {pw ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-[10.5px] text-text-faint shrink-0">Password</span>
                  <code className="font-mono text-[11px] bg-bg/60 px-1.5 py-0.5 rounded border border-border/60 truncate flex-1" title={shown ? pw : '••••••••'}>
                    {shown ? pw : '•'.repeat(Math.min(pw.length, 14))}
                  </code>
                  <button
                    onClick={() => setReveal({ ...reveal, [p.index]: !shown })}
                    className="text-[10.5px] text-text-faint hover:text-text px-1 shrink-0"
                    title={shown ? 'Masquer' : 'Voir'}
                  >{shown ? 'masquer' : 'voir'}</button>
                  <button
                    onClick={() => copy(p.index, pw)}
                    className="text-[10.5px] text-text-faint hover:text-brand px-1 shrink-0"
                    title="Copier"
                  >{copied === p.index ? '✓ copié' : 'copier'}</button>
                </div>
              ) : p.security && p.security !== 'None' ? (
                isCalix ? (
                  <div className="text-[10.5px] text-amber-400/70 italic" title="Le firmware Calix (ExOS) ne retourne jamais le mot de passe en lecture (KeyPassphrase write-only). Il peut être modifié, pas affiché.">
                    🔒 Non lisible sur Calix (write-only) — modifiable mais pas affichable
                  </div>
                ) : (
                  <div className="text-[10.5px] text-text-faint italic">Password pas encore lu depuis le CPE — cliquer « Rafraîchir ports ».</div>
                )
              ) : (
                <div className="text-[10.5px] text-text-faint italic">Réseau ouvert (pas de password).</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Speedtest history card ────────────────────────────────────────────────
type SpeedtestRow = {
  id: number;
  device_id: number;
  server: string | null;
  status: 'pending' | 'complete' | 'failed' | null;
  download_mbps: string | number | null;
  upload_mbps: string | number | null;
  latency_ms: string | number | null;
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
  created_at: string;
  raw_result: string | null;
};

// Traduit un code d'erreur TR-143 (ou interne) en libellé lisible.
function speedtestErrorLabel(err: string | null): string {
  if (!err) return 'Échec';
  if (err.startsWith('cpe_denied')) {
    const code = err.split(':')[1];
    return `Le CPE a refusé le test TR-143 (firmware)${code ? ` — fault ${code}` : ''}`;
  }
  const map: Record<string, string> = {
    timeout: 'Aucune réponse du CPE (test coincé — URL injoignable ou firmware)',
    queue_failed: 'Échec de la mise en file NBI',
    Error_CannotResolveHostName: 'DNS : nom du serveur introuvable',
    Error_NoResponse: 'Serveur de test injoignable',
    Error_Timeout: 'Timeout côté CPE',
    Error_TransferFailed: 'Transfert interrompu',
    Error_IncorrectSize: 'Taille de fichier inattendue',
    Error_InitConnectionFailed: 'Connexion au serveur impossible',
    Error_NoRouteToHost: 'Pas de route vers le serveur',
  };
  return map[err] ?? err;
}

function SpeedtestCard({ deviceId, onRun, running, supported }: { deviceId: number; onRun: () => void; running: boolean; supported: boolean }) {
  const { data } = useQuery({
    queryKey: ['device-speedtests', deviceId],
    queryFn: () => devices.speedtests(deviceId) as Promise<SpeedtestRow[]>,
    // Polling serré tant qu'un test est en vol, sinon lent.
    refetchInterval: (q) => {
      if (!supported) return false;
      const rows = (q.state.data as SpeedtestRow[] | undefined) ?? [];
      return rows.some((r) => r.status === 'pending') || running ? 4_000 : 20_000;
    },
    enabled: supported,
  });
  const rows = data ?? [];
  const latest = rows[0];
  const isPending = latest?.status === 'pending';
  const isFailed = latest?.status === 'failed';

  if (!supported) {
    return (
      <div className="card opacity-60">
        <div className="px-3 py-2.5 border-b border-border flex items-center justify-between">
          <h3 className="font-semibold text-sm flex items-center gap-2 text-text-muted">
            <Gauge size={13} />Speedtests
            <span className="text-text-faint text-[10.5px] font-normal">TR-143 · DownloadDiagnostics</span>
          </h3>
          <button className="btn btn-sm cursor-not-allowed" disabled title="Non supporté par ce firmware">
            <Gauge size={11} />Non disponible
          </button>
        </div>
        <div className="px-3 py-5 text-xs text-text-faint">
          <div className="font-medium text-text-muted mb-1">Pas supporté par ce CPE</div>
          Le firmware Calix refuse l'armement TR-143 (<code className="font-mono text-[10.5px]">9001 Request Denied</code>) — vérifié sur GS4220E R25 et GS5229E R26.
          <br />
          <span className="text-text-muted">Les SmartRG utilisent cette fonctionnalité normalement.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="px-3 py-2.5 border-b border-border flex items-center justify-between">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Gauge size={13} className="text-text-muted" />Speedtests
          <span className="text-text-faint text-[10.5px] font-normal">TR-143 · DownloadDiagnostics</span>
        </h3>
        <button className="btn btn-sm btn-primary" onClick={onRun} disabled={running || isPending}>
          <Gauge size={11} />{running || isPending ? 'En cours…' : 'Lancer un test'}
        </button>
      </div>
      {latest && isPending && (
        <div className="px-3 py-3 border-b border-border bg-warning/10 flex items-center gap-3">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-warning animate-pulse shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-warning">Test en cours…</div>
            <div className="text-[11px] text-text-muted mt-0.5">
              Le CPE télécharge et rapporte au prochain contact (~1-2 min). Lancé {relTime(latest.started_at ?? latest.created_at)}.
            </div>
          </div>
        </div>
      )}
      {latest && isFailed && (
        <div className="px-3 py-3 border-b border-border bg-danger/10">
          <div className="text-sm font-medium text-danger flex items-center gap-2">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-danger shrink-0" />Échec du test
          </div>
          <div className="text-[11px] text-text-muted mt-1">
            {speedtestErrorLabel(latest.error)} · {relTime(latest.completed_at ?? latest.created_at)}
          </div>
        </div>
      )}
      {latest && !isPending && !isFailed && (
        <div className="px-3 py-3 grid grid-cols-3 gap-3 border-b border-border bg-bg-2">
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-text-faint">Dernier download</div>
            <div className="font-mono text-xl font-medium text-success">{Number(latest.download_mbps ?? 0).toFixed(2)}<span className="text-text-faint text-sm ml-1">Mbps</span></div>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-text-faint">Serveur</div>
            <div className="text-xs mt-1">{latest.server ?? '—'}</div>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-text-faint">Quand</div>
            <div className="text-xs mt-1">{relTime(latest.completed_at ?? latest.created_at)}</div>
          </div>
        </div>
      )}
      {rows.length === 0 && (
        <div className="px-3 py-6 text-center text-text-faint text-xs">
          Aucun speedtest encore. Clique "Lancer un test" — résultat dans 30-60s.
        </div>
      )}
      {rows.length > 1 && (
        <table className="luc-table">
          <thead><tr><th>Date</th><th>Download</th><th>État</th><th>Serveur</th></tr></thead>
          <tbody>
            {rows.slice(1, 15).map((r) => (
              <tr key={r.id}>
                <td className="text-xs">{new Date(r.completed_at ?? r.created_at).toLocaleString('fr-CA')}</td>
                <td className="font-mono text-xs">{r.status === 'complete' || r.status == null ? `${Number(r.download_mbps ?? 0).toFixed(2)} Mbps` : '—'}</td>
                <td className="text-xs">
                  {r.status === 'failed'
                    ? <span className="text-danger" title={speedtestErrorLabel(r.error)}>échec</span>
                    : r.status === 'pending'
                      ? <span className="text-warning">en cours…</span>
                      : <span className="text-success">ok</span>}
                </td>
                <td className="text-text-muted text-xs">{r.server ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Per-host RSSI + link rate history chart ──────────────────────────────
function HostHistoryChart({ deviceId, mac }: { deviceId: number; mac: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['host-history', deviceId, mac],
    queryFn: () => devices.hostHistory(deviceId, mac, 24),
    refetchInterval: 60_000,
  });

  // Signal + PHY rate par timestamp (t = ms epoch pour scale temporel)
  const points = new Map<number, { t: number; signal_dbm?: number; phy_rx_mbps?: number; phy_tx_mbps?: number }>();
  (data?.metrics ?? []).forEach((r) => {
    const t = new Date(r.time).getTime();
    if (!points.has(t)) points.set(t, { t });
    const p = points.get(t)!;
    if (r.metric === 'wifi.host.signal_dbm') p.signal_dbm = r.value;
    else if (r.metric === 'wifi.host.phy_rx_kbps') p.phy_rx_mbps = r.value / 1000;
    else if (r.metric === 'wifi.host.phy_tx_kbps') p.phy_tx_mbps = r.value / 1000;
  });
  const sigRows = Array.from(points.values()).sort((a, b) => a.t - b.t);

  // Bandwidth réelle par-host (rate calculé sur delta bytes)
  const bwRows = (data?.bandwidth ?? []).map((r) => ({
    t: new Date(r.time).getTime(),
    dl_mbps: r.rate_in_bps ? r.rate_in_bps / 1e6 : null,
    ul_mbps: r.rate_out_bps ? r.rate_out_bps / 1e6 : null,
  }));

  return (
    <div className="pt-3 border-t border-border space-y-3">
      <div className="text-[10.5px] uppercase tracking-wider text-text-faint flex items-center justify-between">
        <span>Historique 24h</span>
        <span className="normal-case text-text-faint">{sigRows.length} pts signal · {bwRows.length} pts bandwidth</span>
      </div>

      {isLoading ? (
        <div className="text-text-faint text-[11px]">Chargement…</div>
      ) : sigRows.length === 0 && bwRows.length === 0 ? (
        <div className="bg-bg-2 border border-border rounded p-3 text-text-faint text-[11px]">
          Pas encore de data. Le scheduler collecte aux ~15 min (PeriodicInform Calix). Reviens plus tard.
        </div>
      ) : (
        <>
          {bwRows.length > 0 && (
            <div>
              <div className="text-[10.5px] text-text-faint mb-1">Bande passante consommée (Mbps)</div>
              <ResponsiveContainer width="100%" height={120}>
                <LineChart data={bwRows}>
                  <CartesianGrid stroke="#2e3038" strokeDasharray="2 2" />
                  <XAxis dataKey="t" type="number" scale="time" domain={['dataMin', 'dataMax']}
                    stroke="#6c7079" fontSize={9} tickFormatter={fmtTickTime} />
                  <YAxis stroke="#6c7079" fontSize={9} unit=" Mbps" />
                  <Tooltip contentStyle={{ background: '#21232a', border: '1px solid #3a3c46', fontSize: 11 }} labelFormatter={fmtTooltipTime} />
                  <Line type="monotone" dataKey="dl_mbps" stroke="#56b4e9" strokeWidth={1.5} dot={false} name="Download Mbps" />
                  <Line type="monotone" dataKey="ul_mbps" stroke="#009e73" strokeWidth={1.5} dot={false} name="Upload Mbps" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          {sigRows.length > 0 && (
            <div>
              <div className="text-[10.5px] text-text-faint mb-1">Signal RSSI (dBm)</div>
              <ResponsiveContainer width="100%" height={100}>
                <LineChart data={sigRows}>
                  <CartesianGrid stroke="#2e3038" strokeDasharray="2 2" />
                  <XAxis dataKey="t" type="number" scale="time" domain={['dataMin', 'dataMax']}
                    stroke="#6c7079" fontSize={9} tickFormatter={fmtTickTime} />
                  <YAxis stroke="#6c7079" fontSize={9} domain={[-90, -30]} unit=" dBm" />
                  <Tooltip contentStyle={{ background: '#21232a', border: '1px solid #3a3c46', fontSize: 11 }} labelFormatter={fmtTooltipTime} />
                  <Line type="monotone" dataKey="signal_dbm" stroke="#f0b400" strokeWidth={1.5} dot={false} name="RSSI" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          {sigRows.length > 0 && (
            <div>
              <div className="text-[10.5px] text-text-faint mb-1">PHY rate négocié (Mbps)</div>
              <ResponsiveContainer width="100%" height={100}>
                <LineChart data={sigRows}>
                  <CartesianGrid stroke="#2e3038" strokeDasharray="2 2" />
                  <XAxis dataKey="t" type="number" scale="time" domain={['dataMin', 'dataMax']}
                    stroke="#6c7079" fontSize={9} tickFormatter={fmtTickTime} />
                  <YAxis stroke="#6c7079" fontSize={9} unit=" Mbps" />
                  <Tooltip contentStyle={{ background: '#21232a', border: '1px solid #3a3c46', fontSize: 11 }} labelFormatter={fmtTooltipTime} />
                  <Line type="monotone" dataKey="phy_rx_mbps" stroke="#56b4e9" strokeWidth={1.2} dot={false} name="PHY Rx" />
                  <Line type="monotone" dataKey="phy_tx_mbps" stroke="#009e73" strokeWidth={1.2} dot={false} name="PHY Tx" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function bandFromStandard(s?: string | null, channel?: number | string | null, ssid?: string | null): string {
  // SSID hint pour les noms convention type "RESERVED-6G-..." Calix ExOS
  if (ssid && /[-_ ]6G[-_ ]?/i.test(ssid)) return '6 GHz';
  const ch = typeof channel === 'string' ? parseInt(channel, 10) : channel;
  if (typeof ch === 'number' && Number.isFinite(ch)) {
    if (ch === 0) return '—';
    if (ch >= 1 && ch <= 14) return '2.4 GHz';
    if (ch >= 36 && ch <= 177) return '5 GHz';
    if (ch >= 181) return '6 GHz';
  }
  // Fallback: standard. ax/axe ne distingue pas, ac=5 always, b/g/n=2.4 usually.
  if (!s) return '—';
  const std = s.toLowerCase();
  if (/^ac/.test(std)) return '5 GHz';
  if (/^(b|g|n)/.test(std)) return '2.4 GHz';
  return '—';
}

// ─── WiFi edit modal ───────────────────────────────────────────────────────
function WifiEditModal({
  open, onClose, ports, onSubmit, submitting,
}: {
  open: boolean;
  onClose: () => void;
  ports: Port[];
  onSubmit: (items: Array<{ index: string; ssid?: string; password?: string }>) => void;
  submitting: boolean;
}) {
  const wifi = ports.filter((p) => p.type === 'wifi');
  const [edits, setEdits] = useState<Record<string, { ssid: string; password: string }>>({});
  const [showPw, setShowPw] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (open && wifi.length > 0) {
      const init: Record<string, { ssid: string; password: string }> = {};
      wifi.forEach((p) => { init[p.index] = { ssid: p.ssid ?? '', password: '' }; });
      setEdits(init);
    } else if (!open) {
      setEdits({});
      setShowPw({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ports.length]);

  function submit() {
    // Le mapping vers les paths TR-069 est fait côté serveur par le DRIVER
    // (TR-098 Calix vs TR-181 SmartRG) — l'UI n'envoie que les changements.
    const items: Array<{ index: string; ssid?: string; password?: string }> = [];
    wifi.forEach((p) => {
      const e = edits[p.index];
      if (!e) return;
      const item: { index: string; ssid?: string; password?: string } = { index: p.index };
      if (e.ssid && e.ssid !== p.ssid) item.ssid = e.ssid;
      if (e.password) item.password = e.password;
      if (item.ssid || item.password) items.push(item);
    });
    if (items.length === 0) {
      onClose();
      return;
    }
    onSubmit(items);
  }

  return (
    <Modal open={open} onClose={onClose} title="Modifier WiFi" width="max-w-2xl">
      <div className="space-y-3">
        <p className="text-[11px] text-text-muted">
          Laisse le password vide pour ne pas le changer. Password WPA : 8-63 caractères. Le CPE
          applique au prochain Inform (≤15 min selon le modèle) ou immédiatement si le Connection Request aboutit.
        </p>
        {wifi.map((p) => {
          const band = p.band ?? bandFromStandard(p.standard, p.channel);
          const e = edits[p.index] ?? { ssid: '', password: '' };
          return (
            <div key={p.index} className="card card-pad space-y-2 bg-bg-2">
              <div className="flex items-center justify-between">
                <div className="text-xs font-medium">
                  {band} · SSID {p.index}{p.ssid ? ` · ${p.ssid}` : ''}
                </div>
                <div className="text-[10.5px] text-text-faint">{p.standard} · ch {p.channel ?? '—'}</div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10.5px] text-text-faint uppercase tracking-wider">SSID</label>
                  <input
                    className="input w-full mt-1"
                    value={e.ssid}
                    maxLength={32}
                    onChange={(ev) => setEdits({ ...edits, [p.index]: { ...e, ssid: ev.target.value } })}
                  />
                </div>
                <div>
                  <label className="text-[10.5px] text-text-faint uppercase tracking-wider">
                    Password {p.passphrase ? <span className="normal-case text-text-muted">· actuel: <code className="font-mono">{showPw[p.index] ? p.passphrase : '•'.repeat(Math.min(p.passphrase.length, 10))}</code></span> : '(laisse vide pour ne pas modifier)'}
                  </label>
                  <div className="relative">
                    <input
                      className="input w-full mt-1 pr-16"
                      type={showPw[p.index] ? 'text' : 'password'}
                      value={e.password}
                      placeholder="••••••••"
                      minLength={8}
                      maxLength={63}
                      onChange={(ev) => setEdits({ ...edits, [p.index]: { ...e, password: ev.target.value } })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw({ ...showPw, [p.index]: !showPw[p.index] })}
                      className="absolute right-2 top-1/2 -translate-y-0 text-[10.5px] text-text-faint hover:text-text"
                      style={{ top: 'calc(50% + 2px)' }}
                    >
                      {showPw[p.index] ? 'Masquer' : 'Voir'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {wifi.length === 0 && (
          <div className="text-text-faint text-xs py-4 text-center">
            Aucun radio WiFi détecté. Clique "Rafraîchir ports" sur la carte ports d'abord.
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn btn-sm" onClick={onClose} disabled={submitting}>Annuler</button>
          <button className="btn btn-sm btn-primary" onClick={submit} disabled={submitting || wifi.length === 0}>
            {submitting ? 'Push en cours…' : 'Pousser au CPE'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

type ActionItem = { label: string; onClick: () => void; disabled?: boolean; danger?: boolean; icon?: React.ReactNode };
function ActionMenu({ items }: { items: ActionItem[] }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [open]);
  if (items.length === 0) return null;
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button className="btn btn-sm" onClick={() => setOpen((v) => !v)} aria-label="Plus d'actions">
        <MoreHorizontal size={13} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 z-30 bg-bg-2 border border-border rounded shadow-lg py-1 min-w-[180px]">
          {items.map((it, i) => (
            <button
              key={i}
              disabled={it.disabled}
              onClick={() => { setOpen(false); it.onClick(); }}
              className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-surface-hi disabled:opacity-40 ${it.danger ? 'text-warning' : 'text-text'}`}>
              {it.icon}<span>{it.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FirmwareUpdateModal({ device, onClose }: { device: { id: number; serial_number: string; firmware_version: string | null; model?: { manufacturer: string; model_name: string } | null }; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<number | null>(null);

  const { data: allFw = [], isLoading } = useQuery({
    queryKey: ['firmware'],
    queryFn: () => fwApi.list(),
  });
  const { data: history = [] } = useQuery({
    queryKey: ['firmware-updates', device.id],
    queryFn: () => fwApi.history(device.id),
  });

  const mfg = (device.model?.manufacturer ?? '').toLowerCase();
  const mdl = (device.model?.model_name ?? '').toLowerCase();
  const compat = (allFw as FirmwareRelease[]).filter((f) => {
    const fv = f.vendor.toLowerCase();
    const fm = f.model.toLowerCase();
    return mfg.includes(fv) || fv.includes(mfg) || mdl.includes(fm) || fm.includes(mdl);
  });
  const other = (allFw as FirmwareRelease[]).filter((f) => !compat.includes(f));

  const [showConfirm, setShowConfirm] = useState(false);
  const push = useMutation({
    mutationFn: () => fwApi.pushTo(device.id, selected!),
    onSuccess: (r) => {
      if (r.ok) {
        toast.push({ kind: 'success', title: 'Push firmware initié — CPE va télécharger + reboot dans 1-5 min' });
        qc.invalidateQueries({ queryKey: ['firmware-updates', device.id] });
        onClose();
      } else {
        setShowConfirm(false);
        toast.push({ kind: 'error', title: `Push échoué: ${r.fault ?? 'unknown'}` });
      }
    },
    onError: (e: unknown) => {
      setShowConfirm(false);
      const err = e as { response?: { data?: { message?: string } } };
      toast.push({ kind: 'error', title: err?.response?.data?.message ?? 'Erreur push firmware' });
    },
  });

  return (
    <Modal open onClose={onClose} title={`Mettre à jour firmware — ${device.serial_number}`} width="max-w-xl">
      <div className="p-4 space-y-4">
        <div className="card p-3 border-warning/40 bg-warning/5">
          <p className="text-xs text-warning font-semibold">⚠ Opération destructive</p>
          <p className="text-[11px] text-text-muted mt-1">
            Le CPE va télécharger l'image (1-5 min selon taille + débit), puis <strong>rebooter</strong> et flasher.
            <br />Service interrompu ~3-5 min. Aucun rollback automatique en cas d'échec.
          </p>
        </div>

        <div>
          <p className="text-[11px] text-text-faint uppercase mb-1">Firmware actuel</p>
          <p className="font-mono text-sm">{device.firmware_version ?? '—'}</p>
        </div>

        <div>
          <p className="text-[11px] text-text-faint uppercase mb-2">
            Compatible <span className="text-text-muted normal-case">({device.model?.manufacturer ?? '?'} {device.model?.model_name ?? '?'})</span>
          </p>
          {isLoading && <p className="text-xs text-text-faint">Chargement…</p>}
          {!isLoading && compat.length === 0 && (
            <p className="text-xs text-text-muted italic">
              Aucun firmware compatible dans le store. <a href="/app/settings" className="text-brand">Uploader →</a>
            </p>
          )}
          <div className="space-y-1">
            {compat.map((f) => (
              <FirmwareRow key={f.id} fw={f} selected={selected === f.id} onSelect={() => setSelected(f.id)} />
            ))}
          </div>
        </div>

        {other.length > 0 && (
          <details className="text-xs text-text-faint">
            <summary className="cursor-pointer hover:text-text">Voir les firmwares non-compatibles ({other.length})</summary>
            <div className="space-y-1 mt-2">
              {other.map((f) => (
                <FirmwareRow key={f.id} fw={f} selected={selected === f.id} onSelect={() => setSelected(f.id)} dimmed />
              ))}
            </div>
          </details>
        )}

        {history.length > 0 && (
          <div>
            <p className="text-[11px] text-text-faint uppercase mb-1">Historique récent</p>
            <div className="text-[11px] text-text-muted space-y-0.5 font-mono">
              {history.slice(0, 5).map((h) => (
                <div key={h.id}>
                  {relTime(h.triggered_at)} · {h.vendor}-{h.model} {h.version} · <span className={h.status === 'applied' ? 'text-success' : h.status === 'failed' ? 'text-danger' : 'text-warning'}>{h.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* In-modal loading state — visible feedback that something is happening */}
        {push.isPending && (
          <div className="card p-3 border-info/40 bg-info/5 flex items-center gap-3">
            <RefreshCw size={14} className="animate-spin text-info shrink-0" />
            <div className="flex-1">
              <p className="text-xs font-semibold text-info">Push en cours…</p>
              <p className="text-[10.5px] text-text-muted mt-0.5">
                Enqueue Download RPC dans GenieACS → CR vers le CPE → réception au prochain Inform.
                Peut prendre 10-30s. Ne ferme pas la fenêtre.
              </p>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button className="btn btn-sm" onClick={onClose} disabled={push.isPending}>Annuler</button>
          {!showConfirm ? (
            <button
              className="btn btn-primary btn-sm text-danger"
              disabled={!selected || push.isPending}
              onClick={() => setShowConfirm(true)}>
              <Cable size={11} />Pousser le firmware
            </button>
          ) : (
            <>
              <span className="text-xs text-warning self-center mr-2">⚠ confirmer ?</span>
              <button className="btn btn-sm" onClick={() => setShowConfirm(false)} disabled={push.isPending}>
                Non
              </button>
              <button
                className="btn btn-primary btn-sm text-danger"
                disabled={push.isPending}
                onClick={() => push.mutate()}>
                <Cable size={11} />{push.isPending ? 'Push en cours…' : 'Oui, pousser'}
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

function FirmwareRow({ fw, selected, onSelect, dimmed }: { fw: FirmwareRelease; selected: boolean; onSelect: () => void; dimmed?: boolean }) {
  return (
    <label className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${selected ? 'border-brand bg-brand/10' : 'border-border hover:bg-surface-hi'} ${dimmed ? 'opacity-60' : ''}`}>
      <input type="radio" checked={selected} onChange={onSelect} />
      <span className="font-mono text-xs text-text">{fw.vendor} {fw.model}</span>
      <span className="font-mono text-xs text-text-muted">v{fw.version}</span>
      {fw.tenant_id === null && <span className="badge bg-info/10 text-info border-info/30 text-[10px]">officiel</span>}
      <span className="ml-auto text-[11px] text-text-faint">{fw.file_size ? `${(fw.file_size / 1024 / 1024).toFixed(1)} MB` : '—'}</span>
    </label>
  );
}

function ApplyTemplateConfirmModal({ deviceId, onClose, onConfirm }: { deviceId: number; onClose: () => void; onConfirm: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['preview-template', deviceId],
    queryFn: () => devices.previewTemplate(deviceId),
  });
  const noTemplate = data?.error === 'no_template_resolvable' || !data?.template;

  return (
    <Modal open onClose={onClose} title="Appliquer un template" width="max-w-xl">
      <div className="p-4 space-y-4">
        {isLoading && <p className="text-xs text-text-faint">Calcul du diff…</p>}

        {!isLoading && noTemplate && (
          <div className="card p-3 border-warning/40 bg-warning/5">
            <p className="text-xs text-warning font-semibold">Aucun template applicable</p>
            <p className="text-[11px] text-text-muted mt-1">
              Ce device n'a pas de service plan associé et aucun template par défaut n'existe pour le tenant.
              Liez d'abord un customer avec un plan, ou créez un template par défaut.
            </p>
          </div>
        )}

        {!isLoading && !noTemplate && data && (
          <>
            <div>
              <p className="text-[11px] text-text-faint uppercase mb-1">Template</p>
              <p className="text-sm text-text font-medium">{data.template?.name}</p>
            </div>

            {data.warnings.length > 0 && (
              <div className="card p-3 border-warning/40 bg-warning/5">
                <p className="text-xs text-warning font-semibold mb-1">⚠ Attention</p>
                <ul className="text-[11px] text-text-muted space-y-1 list-disc pl-4">
                  {data.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            <div>
              <p className="text-[11px] text-text-faint uppercase mb-1">
                Changements à pousser ({data.changes.length})
              </p>
              <div className="card divide-y divide-border text-xs max-h-72 overflow-auto">
                {data.changes.map((c, i) => (
                  <div key={i} className="px-3 py-1.5 grid grid-cols-[160px_1fr] gap-2">
                    <span className="text-text-muted">{c.label}</span>
                    <span className={`font-mono ${c.kind === 'wifi_password' ? 'text-warning' : 'text-text'}`}>
                      {c.value}
                    </span>
                  </div>
                ))}
                {data.changes.length === 0 && (
                  <div className="px-3 py-3 text-text-faint italic text-center">Aucun changement (template vide)</div>
                )}
              </div>
            </div>

            <p className="text-[11px] text-text-faint leading-relaxed">
              Ces paramètres seront poussés au CPE via SetParameterValues. Le device les
              appliquera au prochain Inform (déclenché immédiatement par Connection Request).
            </p>
          </>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button className="btn btn-sm" onClick={onClose}>Annuler</button>
          <button
            className="btn btn-primary btn-sm"
            disabled={isLoading || noTemplate}
            onClick={onConfirm}>
            <Sparkles size={11} />Appliquer
          </button>
        </div>
      </div>
    </Modal>
  );
}

function StateBadge({ state }: { state?: string | null }) {
  const map: Record<string, { label: string; cls: string }> = {
    discovered: { label: 'Discovered', cls: 'bg-warning/10 text-warning border-warning/30' },
    adopted:    { label: 'Adopted (docked)', cls: 'bg-info/10 text-info border-info/30' },
    provisioned:{ label: 'Provisioned', cls: 'bg-success/10 text-success border-success/30' },
  };
  const s = state && map[state] ? map[state] : { label: state ?? '?', cls: 'bg-bg-2 text-text-faint border-border' };
  return <span className={`badge text-[10.5px] ${s.cls}`}>{s.label}</span>;
}

function Tile({ label, value, colorClass = 'text-text', mono }: { label: string; value: React.ReactNode; colorClass?: string; mono?: boolean }) {
  return (
    <div className="stat-tile">
      <span className="stat-label">{label}</span>
      <span className={`stat-value ${colorClass} ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}

// ─── HealthCard : analyse heuristique ─ → AI v2 ─────────────────────────────
function HealthCard({ deviceId }: { deviceId: number }) {
  const { data } = useQuery({
    queryKey: ['device-health', deviceId],
    queryFn: () => devices.health(deviceId),
    refetchInterval: 60_000,
  });
  if (!data) return null;

  const statusColor =
    data.overall_status === 'healthy'  ? 'border-success bg-success/5 text-success' :
    data.overall_status === 'critical' ? 'border-danger bg-danger/5 text-danger' :
    data.overall_status === 'warning'  ? 'border-warning bg-warning/5 text-warning' :
                                          'border-info bg-info/5 text-info';
  const statusLabel =
    data.overall_status === 'healthy'  ? 'En santé' :
    data.overall_status === 'critical' ? 'Critique' :
    data.overall_status === 'warning'  ? 'Attention' : 'Info';

  return (
    <div className={`card border-l-2 ${statusColor.split(' ').filter(c => c.startsWith('border-')).join(' ')}`}>
      <div className="px-3 py-2.5 border-b border-border flex items-center justify-between">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Activity size={13} className={statusColor.split(' ').find(c => c.startsWith('text-'))} />
          Analyse santé · 24h
          <span className={`badge text-[10.5px] ${statusColor.split(' ').filter(c => c.startsWith('bg-') || c.startsWith('text-')).join(' ')}`}>
            {statusLabel}
          </span>
        </h3>
        <span className="text-text-faint text-[10.5px]">
          basé sur règles heuristiques · AI Claude v2 à venir
        </span>
      </div>

      {data.alerts.length === 0 ? (
        <div className="px-3 py-4 text-xs text-success/80">
          ✓ Aucune anomalie détectée. Tous les seuils sont dans la norme sur les dernières 24h.
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {data.alerts.map((a, i) => {
            const sevColor = a.severity === 'critical' ? 'text-danger' : a.severity === 'warning' ? 'text-warning' : 'text-info';
            const sevIcon = a.severity === 'critical' ? '●' : a.severity === 'warning' ? '▲' : 'ⓘ';
            return (
              <li key={i} className="px-3 py-2.5">
                <div className="flex items-start gap-2.5">
                  <span className={`${sevColor} text-sm shrink-0 mt-px`}>{sevIcon}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium">{a.title}</div>
                    <div className="text-[11px] text-text-muted leading-snug mt-0.5">{a.message}</div>
                    <div className="text-[10.5px] text-text-faint mt-1 font-mono">{a.kind} · {a.subject}</div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ─── MiniChart : utilisé dans les dashboards WiFi Health / CPE Health ───────
function MiniChart({
  title, hint, unit, data, lines, yDomain,
}: {
  title: string;
  hint?: string;
  unit?: string;
  data: Array<Record<string, unknown>>;
  lines: Array<{ key: string; color: string; name: string }>;
  yDomain?: [number, number];
}) {
  const hasData = data.some((p) => lines.some((l) => p[l.key] != null));
  return (
    <div className="bg-bg-2 border border-border rounded p-2">
      <div className="flex items-baseline justify-between mb-1.5 gap-2">
        <div className="text-[11px] font-medium text-text">{title}</div>
        <div className="flex gap-2 text-[10px] text-text-faint shrink-0">
          {lines.map((l) => (
            <span key={l.key} className="flex items-center gap-1">
              <span className="w-2 h-0.5 inline-block" style={{ background: l.color }} />{l.name}
            </span>
          ))}
        </div>
      </div>
      {hasData ? (
        <ResponsiveContainer width="100%" height={110}>
          <LineChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="#2e3038" strokeDasharray="2 2" />
            <XAxis dataKey="t" type="number" scale="time" domain={['dataMin', 'dataMax']}
              stroke="#6c7079" fontSize={9}
              tickFormatter={fmtTickTime} ticks={timeTicks(data as Array<{ t: number }>)} />
            <YAxis stroke="#6c7079" fontSize={9} unit={unit ? ' ' + unit : undefined} domain={yDomain ?? ['auto', 'auto']} />
            <Tooltip
              contentStyle={{ background: '#21232a', border: '1px solid #3a3c46', fontSize: 11 }}
              labelFormatter={fmtTooltipTime}
            />
            {lines.map((l) => (
              <Line key={l.key} type="monotone" dataKey={l.key} stroke={l.color} strokeWidth={1.4} dot={false} name={l.name} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-[110px] flex items-center justify-center text-text-faint text-[10.5px]">
          Aucune donnée
        </div>
      )}
      {hint && <div className="text-[10.5px] text-text-faint mt-1.5 leading-snug">{hint}</div>}
    </div>
  );
}

function CustomerNotesCard({ deviceId, device }: { deviceId: number; device: Device | undefined }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [notes, setNotes] = useState('');
  const [notesDirty, setNotesDirty] = useState(false);

  useEffect(() => {
    if (device?.notes !== undefined && !notesDirty) setNotes(device.notes ?? '');
  }, [device?.notes, notesDirty]);

  const { data: customerResults } = useQuery({
    queryKey: ['customers-search', search],
    queryFn: () => customers.list({ q: search, per_page: 10 }),
    enabled: search.length >= 2,
  });

  const assignMut = useMutation({
    mutationFn: (customer_id: number | null) =>
      toast.promise(devices.update(deviceId, { customer_id }), {
        loading: 'Mise à jour customer…',
        success: 'Customer mis à jour',
        error: 'Erreur mise à jour',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['device', deviceId] });
      qc.invalidateQueries({ queryKey: ['devices'] });
      setSearch('');
    },
  });

  const saveNotesMut = useMutation({
    mutationFn: () =>
      toast.promise(devices.update(deviceId, { notes }), {
        loading: 'Sauvegarde…',
        success: 'Notes sauvegardées',
        error: 'Erreur sauvegarde',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['device', deviceId] });
      setNotesDirty(false);
    },
  });

  return (
    <div className="card">
      <div className="px-3 py-2.5 border-b border-border">
        <h3 className="font-semibold text-sm">Customer & Notes</h3>
      </div>
      <div className="p-3 space-y-3">
        {/* Customer assignment */}
        <div>
          <div className="text-[10px] uppercase tracking-wide text-text-faint mb-1.5">Customer assigné</div>
          {device?.customer ? (
            <div className="flex items-center justify-between gap-2 p-2 bg-bg-2 rounded">
              <div className="text-sm">
                <Link to={`/app/customers/${device.customer.id}`} className="font-medium hover:text-brand">{device.customer.name}</Link>
                <div className="text-xs text-text-muted">
                  {device.customer.external_id && <span className="font-mono">#{device.customer.external_id}</span>}
                </div>
              </div>
              <button className="btn btn-ghost btn-sm text-danger"
                onClick={() => { if (confirm('Détacher ce customer?')) assignMut.mutate(null); }}>
                Détacher
              </button>
            </div>
          ) : (
            <div className="text-xs text-text-faint italic mb-1.5">Non assigné</div>
          )}

          <div className="relative">
            <input
              type="text"
              className="input w-full text-xs"
              placeholder="Rechercher customer par nom, email, external_id…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search.length >= 2 && customerResults && customerResults.data.length > 0 && (
              <div className="absolute z-10 mt-1 w-full max-h-48 overflow-auto bg-bg-2 border border-border rounded shadow-lg">
                {customerResults.data.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => assignMut.mutate(c.id)}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-surface-hi border-b border-border last:border-b-0"
                  >
                    <div className="font-medium">{c.name}</div>
                    <div className="text-text-faint">
                      {c.external_id && <span className="font-mono mr-2">#{c.external_id}</span>}
                      {c.email && <span>{c.email}</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-wide text-text-faint">Notes</div>
            {notesDirty && (
              <button onClick={() => saveNotesMut.mutate()}
                disabled={saveNotesMut.isPending}
                className="btn btn-primary btn-sm">
                {saveNotesMut.isPending ? 'Sauvegarde…' : 'Enregistrer'}
              </button>
            )}
          </div>
          <textarea
            className="input w-full text-xs"
            rows={4}
            placeholder="Notes techniques, contexte client, problèmes récurrents…"
            value={notes}
            onChange={(e) => { setNotes(e.target.value); setNotesDirty(true); }}
          />
        </div>
      </div>
    </div>
  );
}

function UptimeHistoryCard({ deviceId }: { deviceId: number }) {
  const [days, setDays] = useState<number>(7);
  const { data } = useQuery({
    queryKey: ['device-uptime', deviceId, days],
    queryFn: () => devices.uptimeHistory(deviceId, days),
    refetchInterval: 60000,
  });

  const fmtDur = (sec: number): string => {
    if (sec < 60) return `${sec}s`;
    if (sec < 3600) return `${Math.round(sec / 60)}min`;
    if (sec < 86400) return `${(sec / 3600).toFixed(1)}h`;
    return `${(sec / 86400).toFixed(1)}j`;
  };

  return (
    <div className="card">
      <div className="px-3 py-2.5 border-b border-border flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          {data?.is_online ? <Wifi size={13} className="text-success" /> : <WifiOff size={13} className="text-danger" />}
          Historique disponibilité
        </h3>
        <div className="flex items-center gap-1 bg-bg-2 border border-border rounded text-xs">
          {[1, 7, 30].map((d) => (
            <button key={d} onClick={() => setDays(d)}
              className={`h-6 px-2 ${days === d ? 'bg-surface-hi text-text' : 'text-text-muted hover:text-text'}`}>
              {d}j
            </button>
          ))}
        </div>
      </div>
      <div className="p-3 space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-text-faint">Uptime {days}j</div>
            <div className={`text-xl font-semibold ${(data?.uptime_pct ?? 100) >= 99 ? 'text-success' : (data?.uptime_pct ?? 0) >= 95 ? 'text-warning' : 'text-danger'}`}>
              {data?.uptime_pct !== null && data?.uptime_pct !== undefined ? `${data.uptime_pct}%` : '—'}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-text-faint">Total downtime</div>
            <div className="text-xl font-semibold text-text-muted">{data ? fmtDur(data.total_downtime_sec) : '—'}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-text-faint">Events</div>
            <div className="text-xl font-semibold text-text-muted">{data?.event_count ?? 0}</div>
          </div>
        </div>

        {data?.last_outage && (
          <div className="border-t border-border pt-3">
            <div className="text-[10px] uppercase tracking-wide text-text-faint mb-1">Dernière panne</div>
            <div className="text-xs">
              <span className="text-danger">↓</span> {relTime(data.last_outage.started_at)} →
              <span className="text-success ml-1">↑</span> {relTime(data.last_outage.ended_at)}
              <span className="text-text-muted ml-2">({fmtDur(data.last_outage.duration_sec)})</span>
            </div>
          </div>
        )}

        {data?.events && data.events.length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-text-faint hover:text-text">
              Timeline ({data.events.length} events)
            </summary>
            <div className="mt-2 space-y-1 max-h-64 overflow-y-auto">
              {data.events.slice().reverse().map((e, i) => (
                <div key={i} className="flex items-center gap-2 py-0.5 border-b border-border/50">
                  <span className={e.event_type === 'online' ? 'text-success' : 'text-danger'}>
                    {e.event_type === 'online' ? '↑' : '↓'}
                  </span>
                  <span className="font-mono text-[11px]">{new Date(e.at).toLocaleString('fr-CA', {month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'})}</span>
                  <span className="text-text-faint">{e.event_type}</span>
                  {e.duration_sec && (
                    <span className="text-text-muted ml-auto text-[10px]">downtime {fmtDur(e.duration_sec)}</span>
                  )}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

function ConsumptionCard({ deviceId, from, to, setFrom, setTo }: {
  deviceId: number;
  from: string; to: string;
  setFrom: (v: string) => void; setTo: (v: string) => void;
}) {
  const today = localYmd(new Date());
  const daysAgo = (n: number) => localYmd(new Date(Date.now() - n * 86400000));

  const presets: Array<{ label: string; from: string; to: string }> = [
    { label: 'Aujourd\'hui', from: today, to: today },
    { label: '7j',           from: daysAgo(7),  to: today },
    { label: '30j',          from: daysAgo(30), to: today },
    { label: '90j',          from: daysAgo(90), to: today },
  ];
  const activePreset = presets.find((p) => p.from === from && p.to === to);

  const { data, isLoading } = useQuery({
    queryKey: ['device-consumption', deviceId, from, to],
    queryFn: () => devices.consumption(deviceId, from, to),
    enabled: !!from && !!to,
  });

  const fmt = (bytes: number) => {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let i = 0; let v = bytes;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
  };

  const wanRow = data?.interfaces.find((r) => r.interface === 'WAN');

  return (
    <div className="card">
      <div className="px-3 py-2.5 border-b border-border flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-semibold text-sm">Consommation par période</h3>
        <div className="flex items-center gap-2 text-xs">
          <div className="flex items-center bg-bg-2 border border-border rounded">
            {presets.map((p) => (
              <button
                key={p.label}
                onClick={() => { setFrom(p.from); setTo(p.to); }}
                className={`h-6 px-2 text-[11px] ${activePreset?.label === p.label ? 'bg-surface-hi text-text' : 'text-text-muted hover:text-text'}`}
              >{p.label}</button>
            ))}
          </div>
          <span className="text-text-faint">·</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} max={to}
                 className="bg-bg-2 border border-border rounded px-2 py-1 text-text font-mono [color-scheme:dark]" />
          <span className="text-text-faint">→</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} min={from} max={today}
                 className="bg-bg-2 border border-border rounded px-2 py-1 text-text font-mono [color-scheme:dark]" />
        </div>
      </div>
      <div className="p-3">
        {isLoading ? (
          <div className="text-xs text-text-faint">Calcul…</div>
        ) : !data || data.interfaces.length === 0 ? (
          <div className="text-xs text-text-faint">
            Aucun échantillon de trafic sur cette période.
            {activePreset?.label === "Aujourd'hui" && (
              <span className="block text-[10.5px] text-text-faint/70 mt-0.5">
                La collecte se fait par intervalles ; les premières données du jour apparaissent après quelques cycles.
              </span>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-text-faint">Total WAN download</div>
                <div className="text-xl font-semibold text-blue-300">{wanRow ? fmt(wanRow.rx_bytes) : '—'}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-text-faint">Total WAN upload</div>
                <div className="text-xl font-semibold text-emerald-300">{wanRow ? fmt(wanRow.tx_bytes) : '—'}</div>
              </div>
            </div>
            <details className="text-xs">
              <summary className="cursor-pointer text-text-faint hover:text-text">Breakdown par interface ({data.interfaces.length})</summary>
              <table className="w-full mt-2 text-[11px]">
                <thead className="text-text-faint">
                  <tr><th className="text-left py-1">Interface</th><th className="text-right">RX</th><th className="text-right">TX</th><th className="text-right">Samples</th></tr>
                </thead>
                <tbody>
                  {data.interfaces.map((r) => (
                    <tr key={r.interface} className="border-t border-border">
                      <td className="py-1 font-mono">{r.interface}</td>
                      <td className="text-right">{fmt(r.rx_bytes)}</td>
                      <td className="text-right">{fmt(r.tx_bytes)}</td>
                      <td className="text-right text-text-faint">{r.samples}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          </>
        )}
      </div>
    </div>
  );
}

function ChartCard({ title, children, footer }: { title: string; children: React.ReactNode; footer?: string }) {
  return (
    <div className="card">
      <div className="px-3 py-2.5 border-b border-border">
        <h3 className="font-semibold text-sm">{title}</h3>
      </div>
      <div className="p-3">{children}</div>
      {footer && <div className="px-3 py-2 border-t border-border text-[10.5px] text-text-faint">{footer}</div>}
    </div>
  );
}

function InfoCard({ title, rows }: { title: string; rows: (string[] | [])[] }) {
  return (
    <div className="card">
      <div className="px-3 py-2.5 border-b border-border">
        <h3 className="font-semibold text-sm">{title}</h3>
      </div>
      <div className="p-3 space-y-1.5 text-[12px]">
        {rows.map((r, i) => r.length === 0
          ? <div key={i} className="my-2 border-t border-border/50" />
          : (
            <div key={i} className="flex items-baseline justify-between gap-2">
              <span className="text-text-faint">{r[0]}</span>
              <span className="font-mono text-right">{r[1]}</span>
            </div>
          ))}
      </div>
    </div>
  );
}

// ─── Network info card (WAN IP, LAN subnet, DHCP) ────────────────────────────

function NetworkCard({
  network, onWanPing, wanPingPending,
}: {
  network?: { wan_ip: string | null; lan_ip: string | null; lan_mask: string | null; dhcp_start: string | null; dhcp_end: string | null; dhcp_enable: boolean | string | null };
  onWanPing: (enable: boolean) => void;
  wanPingPending: boolean;
}) {
  if (!network) return null;
  const dhcpOn = network.dhcp_enable === true || network.dhcp_enable === 'true' || network.dhcp_enable === '1';
  return (
    <div className="card">
      <div className="px-3 py-2.5 border-b border-border flex items-center justify-between">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Globe size={13} className="text-text-muted" />Réseau
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-text-faint text-[10.5px]">WAN ping :</span>
          <button
            onClick={() => onWanPing(true)}
            disabled={wanPingPending}
            className="btn btn-sm btn-ghost">Activer</button>
          <button
            onClick={() => onWanPing(false)}
            disabled={wanPingPending}
            className="btn btn-sm btn-ghost">Désactiver</button>
        </div>
      </div>
      <div className="p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <KV label="WAN IP"     value={network.wan_ip}     mono />
        <KV label="LAN gateway" value={network.lan_ip}    mono />
        <KV label="Subnet mask" value={network.lan_mask}  mono />
        <KV label="DHCP server" value={dhcpOn ? 'enabled' : 'disabled'} valueColor={dhcpOn ? 'text-success' : 'text-text-muted'} />
        <KV label="DHCP range" value={network.dhcp_start && network.dhcp_end ? `${network.dhcp_start} → ${network.dhcp_end}` : '—'} mono colSpan={2} />
      </div>
    </div>
  );
}

function KV({ label, value, mono, valueColor = 'text-text', colSpan }: { label: string; value: React.ReactNode; mono?: boolean; valueColor?: string; colSpan?: number }) {
  return (
    <div className={colSpan ? `col-span-${colSpan}` : ''}>
      <div className="text-text-faint text-[10.5px] uppercase tracking-wider">{label}</div>
      <div className={`${mono ? 'font-mono' : ''} ${valueColor} mt-0.5`}>{value ?? '—'}</div>
    </div>
  );
}

// ─── Hosts connectés (depuis filosACS NBI .Hosts.Host.X) ────────────────────

type Host = {
  mac?: string; ip?: string; hostname?: string;
  interface?: string; active?: boolean; is_mesh?: boolean;
  method?: 'wifi' | 'ethernet';
  ssid?: string | null; band?: string | null; channel?: number | string | null;
  lease_remaining?: number;
  signal_dbm?: number | null;
  downlink_kbps?: number | null;
  uplink_kbps?: number | null;
  phy_rx_kbps?: number | null;
  phy_tx_kbps?: number | null;
  wifi_mode?: string | null;
  wifi_uptime_seconds?: number | null;
  bytes_down?: number | null;
  bytes_up?: number | null;
  mimo_rx?: number | null;
  mimo_tx?: number | null;
  pkts_retx_down?: number | null;
};
function fmtBytes(b?: number | null): string {
  if (!b) return '—';
  if (b >= 1e9) return (b / 1e9).toFixed(2) + ' GB';
  if (b >= 1e6) return (b / 1e6).toFixed(1) + ' MB';
  if (b >= 1e3) return (b / 1e3).toFixed(0) + ' kB';
  return b + ' B';
}

// Convertit RSSI dBm en barres 0-4 (Calix Cloud-style)
// -40 et meilleur = 4 barres, -55 = 3, -65 = 2, -75 = 1, pire = 0
function signalBars(dbm?: number | null): number {
  if (dbm == null) return 0;
  if (dbm >= -50) return 4;
  if (dbm >= -60) return 3;
  if (dbm >= -70) return 2;
  if (dbm >= -80) return 1;
  return 0;
}
function signalQuality(dbm?: number | null): 'excellent' | 'good' | 'fair' | 'weak' | 'unknown' {
  if (dbm == null) return 'unknown';
  if (dbm >= -55) return 'excellent';
  if (dbm >= -65) return 'good';
  if (dbm >= -75) return 'fair';
  return 'weak';
}
function SignalBars({ dbm }: { dbm?: number | null }) {
  const bars = signalBars(dbm);
  const q = signalQuality(dbm);
  const color =
    q === 'excellent' ? 'bg-success' :
    q === 'good'      ? 'bg-success/80' :
    q === 'fair'      ? 'bg-warning' :
    q === 'weak'      ? 'bg-danger' : 'bg-text-faint/30';
  return (
    <span className="inline-flex items-end gap-0.5 align-middle" title={dbm != null ? `${dbm} dBm (${q})` : 'inconnu'}>
      {[1, 2, 3, 4].map((i) => (
        <span key={i} className={`w-0.5 rounded-sm ${i <= bars ? color : 'bg-text-faint/20'}`} style={{ height: `${4 + i * 2}px` }} />
      ))}
    </span>
  );
}
function fmtRate(kbps?: number | null): string {
  if (!kbps) return '—';
  if (kbps >= 1000) return `${(kbps / 1000).toFixed(1)} Mbps`;
  return `${kbps} kbps`;
}
function fmtUptime(s?: number | null): string {
  if (!s) return '—';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${s % 60}s`;
}

function HostsCard({ deviceId }: { deviceId: number }) {
  const { data: hosts } = useQuery({
    queryKey: ['device-hosts', deviceId],
    queryFn: () => devices.hosts(deviceId),
    refetchInterval: 30_000,
  });
  const [selected, setSelected] = useState<Host | null>(null);

  const list = (hosts ?? []) as Host[];

  return (
    <>
      <div className="card">
        <div className="px-3 py-2.5 border-b border-border flex items-center justify-between">
          <h3 className="font-semibold text-sm flex items-center gap-2">
            <WifiIcon size={13} className="text-text-muted" />Devices connectés
          </h3>
          <span className="text-text-faint text-xs">{list.length} hosts</span>
        </div>
        <table className="luc-table">
          <thead>
            <tr>
              <th>Active</th>
              <th>Hostname</th>
              <th>IP</th>
              <th>MAC</th>
              <th>Connexion</th>
              <th>SSID / Radio</th>
            </tr>
          </thead>
          <tbody>
            {list.map((h, i) => {
              const isWifi = h.method === 'wifi';
              return (
                <tr key={i} onClick={() => setSelected(h)} className="cursor-pointer">
                  <td><span className={`dot ${h.active ? 'dot-up' : 'dot-down'}`} /></td>
                  <td className="font-medium">
                    {h.hostname ?? '—'}
                    {h.is_mesh && <span className="badge badge-info ml-1">mesh</span>}
                  </td>
                  <td className="font-mono text-[12px] text-text-muted">{h.ip ?? '—'}</td>
                  <td className="font-mono text-[12px] text-text-muted">{h.mac ?? '—'}</td>
                  <td>
                    <span className={`badge ${isWifi ? 'badge-info' : ''}`}>
                      {isWifi ? <WifiIcon size={10} /> : <Cable size={10} />}
                      {isWifi ? 'WiFi' : 'Ethernet'}
                    </span>
                  </td>
                  <td className="text-xs">
                    {isWifi && h.ssid ? (
                      <div className="flex items-center gap-2">
                        <SignalBars dbm={h.signal_dbm} />
                        <div className="flex flex-col">
                          <span className="text-text">{h.ssid}</span>
                          <span className="text-text-faint text-[10.5px] font-mono">
                            {h.band ?? '?'} · ch.{h.channel ?? '?'}{h.signal_dbm != null ? ` · ${h.signal_dbm}dBm` : ''}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <span className="text-text-faint">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {list.length === 0 && (
              <tr><td colSpan={6} className="text-center py-6 text-text-faint">Aucun host visible. Clique "Rafraîchir ports" pour pousser un refresh au CPE.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <HostDetailModal host={selected} onClose={() => setSelected(null)} deviceId={deviceId} />
    </>
  );
}

function HostDetailModal({ host, onClose, deviceId }: { host: Host | null; onClose: () => void; deviceId: number }) {
  if (!host) return null;
  const isWifi = host.method === 'wifi';
  const q = signalQuality(host.signal_dbm);
  return (
    <Modal open={!!host} onClose={onClose} title={host.hostname || host.mac || 'Host'} width="max-w-xl">
      <div className="space-y-4 text-xs">
        {isWifi && host.signal_dbm != null && (
          <div className="bg-bg-2 border border-border rounded px-3 py-3 flex items-center gap-4">
            <SignalBars dbm={host.signal_dbm} />
            <div className="flex-1">
              <div className="text-[10.5px] uppercase tracking-wider text-text-faint">Signal WiFi</div>
              <div className="font-mono text-lg font-medium">{host.signal_dbm} dBm</div>
            </div>
            <div className="text-right">
              <div className="text-[10.5px] uppercase tracking-wider text-text-faint">Qualité</div>
              <div className={`text-sm font-medium ${q === 'excellent' ? 'text-success' : q === 'good' ? 'text-success/80' : q === 'fair' ? 'text-warning' : 'text-danger'}`}>
                {q === 'excellent' ? 'Excellent' : q === 'good' ? 'Bon' : q === 'fair' ? 'Moyen' : q === 'weak' ? 'Faible' : '—'}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <KV label="Hostname" value={host.hostname ?? '—'} />
          <KV label="Statut" value={host.active ? 'Actif' : 'Inactif'} valueColor={host.active ? 'text-success' : 'text-text-faint'} />
          <KV label="IP" value={host.ip ?? '—'} mono />
          <KV label="MAC" value={host.mac ?? '—'} mono />
          <KV label="Connexion" value={isWifi ? 'WiFi' : 'Ethernet'} />
          <KV label="Mesh" value={host.is_mesh ? 'Oui (satellite)' : 'Non'} />
          {isWifi && <>
            <KV label="SSID" value={host.ssid ?? '—'} />
            <KV label="Radio" value={`${host.band ?? '—'} · ch.${host.channel ?? '?'}`} mono />
            <KV label="Mode WiFi" value={host.wifi_mode ? `802.11${host.wifi_mode}` : '—'} />
            <KV label="MIMO" value={host.mimo_rx && host.mimo_tx ? `${host.mimo_rx}x${host.mimo_tx}` : '—'} />
            <KV label="Connecté depuis" value={fmtUptime(host.wifi_uptime_seconds)} />
            <KV label="Retransmits ↓" value={host.pkts_retx_down != null ? host.pkts_retx_down.toLocaleString() : '—'} mono />
            <KV label="Down rate (PHY négocié)" value={fmtRate(host.phy_rx_kbps ?? host.downlink_kbps)} mono valueColor="text-success" />
            <KV label="Up rate (PHY négocié)" value={fmtRate(host.phy_tx_kbps ?? host.uplink_kbps)} mono valueColor="text-info" />
            <KV label="Total téléchargé" value={fmtBytes(host.bytes_down)} mono valueColor="text-success" />
            <KV label="Total uploadé" value={fmtBytes(host.bytes_up)} mono valueColor="text-info" />
          </>}
          {host.lease_remaining != null && host.lease_remaining > 0 && (
            <KV label="DHCP lease" value={`${Math.round(host.lease_remaining / 60)} min restant`} colSpan={2} />
          )}
        </div>

        {isWifi && host.signal_dbm == null && (
          <div className="bg-bg-2 border border-border rounded p-3 text-text-faint text-[11px]">
            Signal non disponible — clique "Forcer refresh" pour pousser un refreshObject sur la branche AssociatedDevice de toutes les WLAN.
          </div>
        )}

        {isWifi && host.mac && <HostHistoryChart deviceId={deviceId} mac={host.mac} />}

        <div className="flex justify-end">
          <button className="btn btn-sm" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Ports state card ────────────────────────────────────────────────────────

function PortsCard({
  ports, lastInform, onRefresh, refreshing,
}: {
  ports: Port[]; lastInform: string | null; onRefresh: () => void; refreshing: boolean;
}) {
  const wan = ports.filter((p) => p.type === 'wan-eth');
  const lan = ports.filter((p) => p.type === 'lan-eth');
  const wifi = ports.filter((p) => p.type === 'wifi');

  return (
    <div className="card">
      <div className="px-3 py-2.5 border-b border-border flex items-center justify-between">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Cable size={13} className="text-text-muted" />
          État des ports
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-text-faint text-[10.5px] font-mono">
            {lastInform ? `Inform il y a ${relTime(lastInform)}` : '—'}
          </span>
          <button onClick={onRefresh} disabled={refreshing} className="btn btn-ghost btn-sm">
            <RefreshCw size={11} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Refresh…' : 'Rafraîchir'}
          </button>
        </div>
      </div>

      <div className="p-3 space-y-3">
        {ports.length === 0 && (
          <div className="text-text-faint text-xs text-center py-4">
            Aucune donnée de port. Clique "Rafraîchir" → filosACS pousse un refreshObject au CPE qui répondra au prochain Inform.
          </div>
        )}

        {wan.length > 0 && (
          <PortGroup label="WAN" icon={Globe} ports={wan} />
        )}
        {lan.length > 0 && (
          <PortGroup label="LAN Ethernet" icon={Cable} ports={lan} />
        )}
        {wifi.length > 0 && (
          <PortGroup label="WiFi (radios actifs)" icon={WifiIcon} ports={wifi} />
        )}
      </div>
    </div>
  );
}

function PortGroup({ label, icon: Icon, ports }: { label: string; icon: React.ElementType; ports: Port[] }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider text-text-faint">
        <Icon size={11} />
        <span>{label}</span>
        <span className="text-text-faint">({ports.length})</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {ports.map((p) => <PortTile key={p.name} port={p} />)}
      </div>
    </div>
  );
}

function PortTile({ port }: { port: Port }) {
  const statusLow = (port.status ?? '').toString().toLowerCase();
  const isUp   = statusLow === 'up'   || statusLow === '1';
  const isDown = statusLow === 'down' || statusLow === 'disabled' || statusLow === 'notpresent';
  const statusClass = isUp ? 'border-success/40 bg-success/5' : isDown ? 'border-danger/40 bg-danger/5' : 'border-border';
  const dotClass = isUp ? 'dot-up' : isDown ? 'dot-down' : 'dot-warn';

  return (
    <div className={`card card-pad ${statusClass} space-y-1`}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs font-medium">{port.name}</span>
        <span className={`dot ${dotClass}`} />
      </div>
      {port.type === 'wifi' ? (
        <>
          <div className="text-[11px] truncate" title={port.ssid || ''}>{port.ssid ?? '—'}</div>
          <div className="text-[10.5px] text-text-faint font-mono">
            ch.{port.channel ?? '?'} · {port.standard ?? '?'}
          </div>
        </>
      ) : (
        <>
          <div className="text-[11px]">
            {isUp
              ? <span className="text-success">Connecté</span>
              : isDown
              ? <span className="text-text-faint">Pas branché</span>
              : <span className="text-warning">Inconnu</span>}
          </div>
          <div className="text-[10.5px] text-text-faint">
            {port.max_bitrate === 'Auto' || !port.max_bitrate
              ? (port.type === 'wan-eth' ? 'auto · jusqu’à 10 G' : 'auto · jusqu’à 1 G')
              : `${port.max_bitrate}${typeof port.max_bitrate === 'number' ? ' Mbps' : ''}`}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Real-data transforms ────────────────────────────────────────────────────

// ─── Time formatters partagés (timezone locale du navigateur) ──────────────
function fmtTickTime(t: number): string {
  const d = new Date(t);
  // Si le tick est minuit → affiche la date jour/mois
  if (d.getHours() === 0 && d.getMinutes() < 30) {
    return `${d.getDate()}/${d.getMonth() + 1}`;
  }
  return d.getHours().toString().padStart(2, '0') + ':' + d.getMinutes().toString().padStart(2, '0');
}
/** Formatter d'axe X conscient de la durée affichée : sur >2 jours on montre des
 *  dates (JJ/MM), sur ~1-2 jours des heures avec repères de date à minuit, en
 *  intraday des heures:minutes. Corrige l'axe figé en HH:MM sur les vues multi-jours. */
function timeTickFmt(spanMs: number): (t: number) => string {
  return (t: number) => {
    const d = new Date(t);
    if (spanMs > 2 * 86_400_000) {
      return `${d.getDate()}/${d.getMonth() + 1}`;
    }
    if (spanMs > 26 * 3_600_000) {
      return d.getHours() === 0 && d.getMinutes() < 30
        ? `${d.getDate()}/${d.getMonth() + 1}`
        : `${d.getHours().toString().padStart(2, '0')}h`;
    }
    return d.getHours().toString().padStart(2, '0') + ':' + d.getMinutes().toString().padStart(2, '0');
  };
}
function fmtTooltipTime(t: number | string): string {
  const d = new Date(typeof t === 'number' ? t : t);
  return d.toLocaleString('fr-CA', { dateStyle: 'short', timeStyle: 'short' });
}
/** Ticks d'axe X. Sur multi-jours (>2j) on place un tick au minuit local de chaque
 *  jour (sous-échantillonné à ~7 max) pour des dates propres ; en intraday, 6 ticks
 *  équirépartis. `spanMs` optionnel = durée de la PÉRIODE demandée (pas juste
 *  l'étendue des points), pour rester cohérent quand la donnée est éparse. */
function timeTicks(rows: Array<{ t: number }>, spanMs?: number): number[] {
  if (rows.length < 2) return rows.map((r) => r.t);
  const first = rows[0].t;
  const last = rows[rows.length - 1].t;
  const span = spanMs ?? (last - first);
  if (span > 2 * 86_400_000) {
    const midnights: number[] = [];
    const d = new Date(first);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 1);  // premier minuit après le début des données
    for (let t = d.getTime(); t <= last; t += 86_400_000) midnights.push(t);
    if (midnights.length === 0) return [first, last];
    const step = Math.ceil(midnights.length / 7);
    return midnights.filter((_, i) => i % step === 0);
  }
  const ticks: number[] = [];
  for (let i = 0; i <= 6; i++) ticks.push(first + ((last - first) * i) / 6);
  return ticks;
}

function bandwidthChartData(rows?: Array<{ time: string; rate_in_bps: number | null; rate_out_bps: number | null }>) {
  // Filter outliers absurdes (> 2 Gbps sur résidentiel WAN = bug de calcul rate)
  const MAX_REASONABLE_BPS = 2_000_000_000;  // 2 Gbps
  return (rows ?? []).map((r) => ({
    t: new Date(r.time).getTime(),
    dl_mbps: r.rate_in_bps  && r.rate_in_bps  < MAX_REASONABLE_BPS ? (r.rate_in_bps  / 1_000_000) : 0,
    ul_mbps: r.rate_out_bps && r.rate_out_bps < MAX_REASONABLE_BPS ? (r.rate_out_bps / 1_000_000) : 0,
  }));
}

/** Pivote les rows {time, metric, value} en {t, metric1: val, metric2: val, ...}
 *  pour une liste de noms de métriques. */
function metricsChartData(
  rows: Array<{ time: string; metric: string; value: number }> | undefined,
  metricNames: string[],
) {
  if (!rows?.length) return [];
  const byTime = new Map<number, Record<string, number>>();
  for (const r of rows) {
    if (!metricNames.includes(r.metric)) continue;
    const t = new Date(r.time).getTime();
    const entry = byTime.get(t) ?? { t };
    entry[r.metric] = r.value;
    byTime.set(t, entry);
  }
  return Array.from(byTime.values()).sort((a, b) => (a.t as number) - (b.t as number));
}

// ─── Edit Params Modal ───────────────────────────────────────────────────────

function EditParamsModal({ deviceId, open, onClose }: { deviceId: number; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [preset, setPreset] = useState(PARAM_PRESETS[0]);
  const [customPath, setCustomPath] = useState('');
  const [value, setValue] = useState('');
  const [result, setResult] = useState<string | null>(null);

  const path = preset.label === 'Custom' ? customPath : preset.path;

  const push = useMutation({
    mutationFn: () => {
      let parsed: string | number | boolean = value;
      if (preset.type === 'number') parsed = Number(value);
      if (preset.type === 'boolean') parsed = value === 'true';
      return devices.setParams(deviceId, { [path]: parsed });
    },
    onSuccess: (r) => {
      setResult(`✓ Pushed ${r.pushed} param(s) à filosACS NBI. Le CPE applique au prochain Inform (≤60s).`);
      qc.invalidateQueries({ queryKey: ['device-events', deviceId] });
    },
    onError: (e) => setResult(`✗ Échec: ${(e as Error).message}`),
  });

  return (
    <Modal open={open} onClose={onClose} title="Modifier paramètre TR-069" width="max-w-xl">
      <div className="space-y-4">
        <div>
          <span className="text-xs text-text-muted block mb-1.5">Paramètre</span>
          <select
            className="input w-full"
            value={preset.label}
            onChange={(e) => {
              const p = [...PARAM_PRESETS, { label: 'Custom', path: '' }].find((x) => x.label === e.target.value)!;
              setPreset(p);
              setValue('');
              setResult(null);
            }}
          >
            {PARAM_PRESETS.map((p) => <option key={p.label} value={p.label}>{p.label}</option>)}
            <option value="Custom">Custom (path TR-069 brut)</option>
          </select>
        </div>

        {preset.label === 'Custom' && (
          <label className="block">
            <span className="text-xs text-text-muted block mb-1.5">Path TR-069</span>
            <input className="input w-full font-mono text-xs" value={customPath} onChange={(e) => setCustomPath(e.target.value)}
              placeholder="InternetGatewayDevice.LANDevice.1..." />
          </label>
        )}

        <div>
          <span className="text-xs text-text-muted block mb-1.5">Path résolu</span>
          <div className="font-mono text-[11px] bg-bg-2 border border-border rounded px-3 py-2 text-text-muted break-all">{path || '(choisis un preset ou path custom)'}</div>
        </div>

        <label className="block">
          <span className="text-xs text-text-muted block mb-1.5">Nouvelle valeur</span>
          <input
            className="input w-full"
            type={preset.type === 'number' ? 'number' : 'text'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={preset.placeholder ?? 'valeur'}
          />
        </label>

        {result && (
          <div className={`card card-pad text-xs ${result.startsWith('✓') ? 'bg-success/5 border-success/30' : 'bg-danger/5 border-danger/30'}`}>
            {result}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn">Fermer</button>
          <button onClick={() => push.mutate()} disabled={push.isPending || !path || !value} className="btn btn-primary">
            {push.isPending ? 'Push…' : 'Push vers le CPE'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
